import React, {useEffect, useMemo, useRef, useState} from "react"
import {Col, Row, TableColumnProps} from "antd";
import styled from "styled-components";
import {
    AdministrationLayout,
    BironTable,
    ContentCard,
    ContentContainer,
    InformationsContainer,
    TableLink
} from "@biron-data/react-components";
import {useSelector} from "react-redux";
import {getAvailablesForAce, getCurrentWorkspaceExpanded} from "../../redux/workspace.selector";
import {useNavigate, useParams} from "react-router-dom";
import {getDataModels} from "../../redux/environment.selector";
import {ConsolidatedAces, getAcesListSelector} from "../../redux/users.selector";
import useDispatch from "../../hooks/useDispatch";
import WorkspaceRightUpdater from "../admin/user/WorkspaceRightUpdater";
import useHandleSingleUserAcesModification from "../../hooks/useHandleSingleUserAcesModification";
import {filterData, Search, SearchRef} from "../search/Search";
import UserManagerInfo from "../admin/user/UserManager.Info";
import {isDatamodelRole, isWorkspaceRole} from "../admin/user/UserManager.utils";
import {
    buildWorkspaceAdminDatamodelUri,
    buildWorkspaceAdminUserUri,
    buildWorkspaceAdminWorkspacesUri
} from "../../commons/buildWorkspaceAdminUri";
import AdminBreadcrumb from "../admin/Admin.Breadcrumb";
import {useFilterAcesByWorkspaceId, useOnUpdateWorkspaceRights} from "./WorkspaceDetail.hooks";
import Language from "../../language";
import {AdminCategory, useGetAdminAccess} from "../../hooks/useGetAdminAccess";

const WorkspaceManager = () => {
    const params = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {id} = params
    const workspaces = useSelector(getAvailablesForAce)
    const currentWorkspace = useSelector(getCurrentWorkspaceExpanded)
    const workspace = workspaces.find(w => w.id === Number(id))
    const datamodels = useSelector(getDataModels)
    const acesFilteredByCurrentEnvironment = useSelector(getAcesListSelector)
    const [search, setSearch] = useState("")

    const acesFilteredByCurrentEnvironmentAndWorkspaceId = useFilterAcesByWorkspaceId(
      acesFilteredByCurrentEnvironment,
      id ?? ""
    )

    const [data, setData] = useState(acesFilteredByCurrentEnvironmentAndWorkspaceId)
    const [loading, setLoading] = useState(true)
    const getAdminAccess = useGetAdminAccess()

    const handleOnConfirm = useHandleSingleUserAcesModification(
      dispatch.users.updateUserAces
    )

    const onUpdateWorkspaceRights = useOnUpdateWorkspaceRights(handleOnConfirm)

    useEffect(() => {
        if (workspace) {
            dispatch.users.loadAcesByWorkspace({workspaceId: workspace.id}).then(() => {
                setLoading(false)
            })
        }
    }, [dispatch, workspace])

    useEffect(() => {
        setData(filterData(acesFilteredByCurrentEnvironmentAndWorkspaceId, (record) => [
            record.firstName,
            record.fullName,
            record.lastName,
        ], search))
    }, [search, acesFilteredByCurrentEnvironmentAndWorkspaceId])

    const columns: TableColumnProps<ConsolidatedAces>[] = useMemo(() => ([
        {
            title: Language.get("admin.workspace.columns.name"),
            dataIndex: 'fullName',
            width: `${30}%`,
            render: (a, b) => <UserManagerInfo user={b} displayEmail={false} onUserNameClick={getAdminAccess(AdminCategory.user) ? () => {
                if (currentWorkspace) {
                    navigate(buildWorkspaceAdminUserUri(currentWorkspace, `${b.id}`))
                }
            } : undefined}/>
        },
        {
            title: Language.get("admin.workspace.columns.email"),
            dataIndex: 'email',
            width: `${50}%`,
        },
        {
            title: Language.get("admin.workspace.columns.access"),
            width: `${20}%`,
            dataIndex: 'aces',
            render: (a, b) => {
                const {aces} = b

                const roles = aces.flatMap(ace => ace.roles)

                return <WorkspaceRightUpdater
                  roles={roles.filter(isWorkspaceRole)}
                  datamodelRights={roles.filter(isDatamodelRole)}
                  onUpdate={(newRoles) => {
                      if (workspace) {
                          setLoading(true)
                          onUpdateWorkspaceRights(b.id, b.aces, newRoles, workspace.id)?.then(() => {
                              setLoading(false)
                          }).catch(() => setLoading(false))
                      }
                  }}/>
            }
        },
    ]), [currentWorkspace, getAdminAccess, navigate, onUpdateWorkspaceRights, workspace])

    const datamodel = useMemo(() => {
        return datamodels.find(d => d.code === workspace?.datamodelCode)
    }, [datamodels, workspace?.datamodelCode])

    const datamodelAlias = useMemo(() => {
        return datamodel?.alias
    }, [datamodel?.alias])

    return <AdministrationLayout breadcrumb={<AdminBreadcrumb
      category={{
          title: Language.get("admin.home.workspaces"),
          link: currentWorkspace && getAdminAccess(AdminCategory.workspaces) ? buildWorkspaceAdminWorkspacesUri(currentWorkspace) : undefined
      }} detail={{
          title: workspace?.name ?? ""
    }}/>}>
        <InformationsContainer>
            <ContentCard title={workspace?.name}>
                        <FlexContainer gap={10}>
                            <Row>
                                <Col span={8}>
                                    <span>{Language.get("admin.workspace.details.datamodel")}:</span>
                                </Col>
                                <Col span={16}>
                                    <TableLink link={currentWorkspace && getAdminAccess(AdminCategory.workspaces) ? buildWorkspaceAdminDatamodelUri(
                                      currentWorkspace,
                                      datamodelAlias ?? ""
                                    ) : ""}>{datamodelAlias}</TableLink>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <span>{Language.get("admin.workspace.details.description")}:</span>
                                </Col>
                                <Col span={16}>
                                    {datamodel?.description ?? ""}
                                </Col>
                            </Row>
                        </FlexContainer>
            </ContentCard>
        </InformationsContainer>
        <ContentContainer>
            <ContentCard title={Language.get("admin.home.users")}>
                    <Search onChange={(newValues) => {
                        setSearch(newValues)
                    }} width={'calc(100% - 25px)'} searchedFieldKey={"admin.user.columns.user"}/>
                    <BironTable
                          columns={columns}
                          loading={loading}
                          dataSource={data}
                          size="middle">
                    </BironTable>
                </ContentCard>
        </ContentContainer>
    </AdministrationLayout>
}

export default WorkspaceManager


const FlexContainer = styled.div<{gap: number}>`
  display: flex;
  flex-direction: column;
    gap:${({gap}) => gap}px;
` // TODO extract in react-components

export const SizeReference = styled.div`
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 0;
`// TODO extract in react-components